import { useCallback, useRef } from 'react';
import { ResourceDefinition } from '../resources/types';
import { ResolverState } from '../resolvers/definition/resolverState';
import { NoParams } from '../resolvers/definition/resolver';

export type Result<TData, TError extends Error = Error> = ResolverState<
  TData,
  TError
>;
import {
  applySelector,
  BaseOptions,
  initial,
  SelectorCache,
  SelectOption,
  skipped,
  SkipToken,
  usePulseClient,
  useParseOptions,
  useSyncResource,
} from './shared';

// Overload: param-using resource WITH select
export function useResource<
  TParams,
  TData,
  TSelected,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions &
    SelectOption<TData, TSelected> & {
      params: { [K in keyof TParams]: TParams[K] } | SkipToken;
    }
): ResolverState<TSelected, TError>;

// Overload: param-using resource WITHOUT select (keep first for intellisense)
// The homomorphic mapped type seems pointless, but is required for full expected property
// checking behavior. I've tagged the relevant test in useResource-types-test.tsx
// with [homomorphic type test]
export function useResource<TParams, TData, TError extends Error = Error>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions & {
    params: { [K in keyof TParams]: TParams[K] } | SkipToken;
  }
): ResolverState<TData, TError>;

// Overload: param-less resource WITH select
export function useResource<
  TParams extends NoParams,
  TData,
  TSelected,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options: BaseOptions &
    SelectOption<TData, TSelected> & { params?: NoParams | SkipToken }
): ResolverState<TSelected, TError>;

// Overload: param-less resource WITHOUT select
export function useResource<
  TParams extends NoParams,
  TData,
  TError extends Error = Error,
>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options?: BaseOptions & { params?: NoParams | SkipToken }
): ResolverState<TData, TError>;

// Implementation
export function useResource<TParams, TData, TError extends Error = Error>(
  resource: ResourceDefinition<TParams, TData, TError>,
  options?: BaseOptions & {
    params?: TParams | SkipToken;
    select?: (data: TData) => unknown;
  }
): ResolverState<any, TError> {
  const {
    params,
    skip,
    pollInterval,
    pollGroup,
    httpClient,
    passive,
    keepAliveWhenHidden,
    instanceKey,
  } = useParseOptions(
    resource as unknown as ResourceDefinition<TParams, any>,
    options
  );

  const client = usePulseClient();

  const resourceForClient = resource as unknown as ResourceDefinition<
    TParams,
    TData
  >;

  const select = options?.select;
  const selectRef = useRef(select);
  // eslint-disable-next-line react-hooks/refs
  selectRef.current = select;

  const cachedRef = useRef<SelectorCache | undefined>(undefined);

  const getSnapshot = useCallback(
    (): ResolverState<any, TError> => {
      const raw = (
        skip || !client
          ? skipped
          : client.getState({
              resource: resourceForClient,
              params,
              instanceKey,
            }) ?? initial
      ) as ResolverState<TData, TError>;

      const selector = selectRef.current;
      if (!selector) {
        return raw;
      }

      const result = applySelector(raw, selector, cachedRef.current);
      cachedRef.current = { source: raw, selector, result };
      return result;
    },

    // instanceKey is a proxy for params and resource changing
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [skip, instanceKey, client]
  );

  return useSyncResource(
    getSnapshot,
    instanceKey,
    resourceForClient,
    params,
    skip,
    pollInterval,
    client,
    httpClient,
    pollGroup,
    passive,
    keepAliveWhenHidden
  );
}
